import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { usePrompts } from '../context/PromptContext';
import { useToast } from '../context/ToastContext';
import { useButtonState } from '../hooks/useButtonState';
import { format } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCopy, FiEdit2, FiCheck, FiAlertCircle, FiTerminal, FiPlus, FiSave, FiChevronDown, FiRotateCcw } = FiIcons;


const PromptDetailsModal = ({ prompt, onClose }) => {
  const { variables: globalVariables, addVariable, addValueToVariable } = usePrompts();
  const { success: showSuccess, error: showError } = useToast();
  const [variables, setVariables] = useState({});
  const [manualContent, setManualContent] = useState(null);
  const [copied, setCopied] = useState(false);
  const [focusedVar, setFocusedVar] = useState(null);
  const [openDropdownVar, setOpenDropdownVar] = useState(null);
  const dropdownRefs = useRef({});
  const [variableSaveStates, setVariableSaveStates] = useState({});

  // Extract placeholders from content
  const placeholders = useMemo(() => {
    if (!prompt?.content) return [];
    const matches = prompt.content.match(/\{[^}]+\}/g) || [];
    return [...new Set(matches)];
  }, [prompt]);

  // Initialize variables state
  useEffect(() => {
    const initialVars = {};
    placeholders.forEach(p => {
      const key = p.replace(/[{}]/g, '');
      initialVars[key] = '';
    });
    setVariables(initialVars);
  }, [prompt, placeholders]);

  // Handle hotkeys
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (openDropdownVar) {
          setOpenDropdownVar(null);
          return;
        }
        onClose();
      }
      // Ctrl/Cmd + Enter to copy
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        handleCopy();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [variables, openDropdownVar]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!openDropdownVar) return;
      const activeRef = dropdownRefs.current[openDropdownVar];
      if (!activeRef) return;
      if (!activeRef.contains(event.target)) {
        setOpenDropdownVar(null);
      }
    };

    document.addEventListener('pointerdown', handleOutsideClick);
    return () => document.removeEventListener('pointerdown', handleOutsideClick);
  }, [openDropdownVar]);


  if (!prompt) return null;

  const handleVariableChange = (key, value) => {
    setVariables(prev => ({ ...prev, [key]: value }));
    setManualContent(null);
  };

  const handleDropdownSelect = (key, value) => {
    handleVariableChange(key, value);
    setOpenDropdownVar(null);
  };

  const toggleDropdown = (key, storedValues) => {
    if (!storedValues || storedValues.length === 0) return;
    setOpenDropdownVar(prev => (prev === key ? null : key));
  };


  const saveValueToGlobal = async (key, value) => {
    if (!value.trim()) return;
    const stateKey = `${key}_save`;

    setVariableSaveStates(prev => ({ ...prev, [stateKey]: 'loading' }));

    try {
      await addValueToVariable(key, value.trim());
      setVariableSaveStates(prev => ({ ...prev, [stateKey]: 'success' }));
      showSuccess(`Value saved for ${key}`);
      setTimeout(() => {
        setVariableSaveStates(prev => ({ ...prev, [stateKey]: 'idle' }));
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save value';
      setVariableSaveStates(prev => ({ ...prev, [stateKey]: 'failed', [`${stateKey}_error`]: errorMsg }));
      showError(errorMsg);
      setTimeout(() => {
        setVariableSaveStates(prev => ({ ...prev, [stateKey]: 'idle' }));
      }, 2000);
    }
  };

  const createVariableAndSave = async (key, value) => {
    const stateKey = `${key}_create`;

    setVariableSaveStates(prev => ({ ...prev, [stateKey]: 'loading' }));

    try {
      if (!value.trim()) {
        await addVariable(key);
      } else {
        await addValueToVariable(key, value.trim());
      }
      setVariableSaveStates(prev => ({ ...prev, [stateKey]: 'success' }));
      showSuccess(`Variable ${key} created successfully`);
      setTimeout(() => {
        setVariableSaveStates(prev => ({ ...prev, [stateKey]: 'idle' }));
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save value';
      setVariableSaveStates(prev => ({ ...prev, [stateKey]: 'failed', [`${stateKey}_error`]: errorMsg }));
      showError(errorMsg);
      setTimeout(() => {
        setVariableSaveStates(prev => ({ ...prev, [stateKey]: 'idle' }));
      }, 2000);
    }
  };

  const getFilledContent = () => {
    let content = prompt.content;
    placeholders.forEach(p => {
      const key = p.replace(/[{}]/g, '');
      const value = variables[key] || p;
      content = content.split(p).join(value);
    });
    return content;
  };

  const displayContent = manualContent !== null ? manualContent : getFilledContent();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  // Removed renderContentPreview as we are now using a textarea for direct editing


  return (
     <div className="fixed inset-0 z-[100] md:flex md:items-center md:justify-center p-0 md:p-4 bg-black/50 backdrop-blur-sm cursor-default">
      <div
        className="bg-white border-2 border-black shadow-neo-xl w-full md:max-w-7xl h-full md:h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="neo-window-header bg-neo-blue flex-shrink-0">
          <div className="flex items-center space-x-2 overflow-hidden">
            <SafeIcon icon={FiTerminal} size={20} />
            <h2 className="text-lg font-black uppercase truncate">{prompt.title}</h2>
          </div>
          <div className="flex items-center space-x-2">
             <Link
               to={`/editor/${prompt.id}`}
               className="p-2 min-w-[44px] min-h-[44px] hover:bg-white border border-transparent hover:border-black transition-all flex items-center justify-center"
               title="Edit Template"
             >
                 <SafeIcon icon={FiEdit2} size={18} />
             </Link>
             <button
               onClick={onClose}
               className="p-2 min-w-[44px] min-h-[44px] hover:bg-red-400 hover:text-white border border-transparent hover:border-black transition-all flex items-center justify-center"
               title="Close (Esc)"
             >
                 <SafeIcon icon={FiX} size={20} />
             </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row pb-safe">

          {/* Left Panel: Variables */}
          <div className="w-full md:w-1/3 bg-gray-50 border-b-2 md:border-b-0 md:border-r-2 border-black flex flex-col">
         <div className="p-4 border-b-2 border-black bg-neo-yellow">
               <h3 className="font-bold text-sm uppercase flex items-center">
                 <span className="bg-black text-white w-11 h-11 flex items-center justify-center rounded-full text-sm mr-2">1</span>
                 Fill Variables
               </h3>
               <p className="text-xs font-mono mt-1 opacity-70">Press TAB to switch fields</p>
            </div>

             <div className="p-4 overflow-y-auto flex-1 space-y-4">
               {placeholders.length === 0 ? (
                 <div className="text-center text-gray-500 py-8 font-mono text-sm">
                   No variables detected in this template.
                 </div>
               ) : (
                 placeholders.map((p, index) => {
                    const key = p.replace(/[{}]/g, '');
                    const storedValues = globalVariables[key];
                    const currentValue = variables[key];
                    const isNewValue = currentValue && storedValues && !storedValues.includes(currentValue);
                    const isNewVariable = !storedValues;

                    const filteredValues = storedValues
                      ? storedValues.filter(val => !variables[key] || val.toLowerCase().includes(variables[key].toLowerCase()))
                      : [];

                    const stateKey = isNewVariable ? `${key}_create` : `${key}_save`;
                    const saveState = variableSaveStates[stateKey] || 'idle';
                    const isError = saveState === 'failed';

                    return (
                       <div key={key} className="relative group">
                         <div className="flex justify-between items-center mb-1">
                             <label className="block text-xs font-bold uppercase">{key}</label>
                             {/* Quick Action Buttons */}
                              {currentValue && (
                                  <div className="flex gap-1">
                                      {isNewVariable ? (
                                          <button
                                              onClick={() => createVariableAndSave(key, currentValue)}
                                              disabled={saveState === 'loading'}
                                              className={`text-xs bg-neo-purple hover:bg-purple-300 border border-black px-3 py-2 min-w-[44px] min-h-[44px] flex items-center transition-all ${saveState === 'loading' ? 'opacity-80 cursor-wait' : ''} ${saveState === 'success' ? 'bg-green-500 text-white' : ''} ${saveState === 'failed' ? 'bg-red-500 text-white' : ''}`}
                                              title={saveState === 'loading' ? 'Creating...' : 'Create Variable & Save Value'}
                                          >
                                              <SafeIcon icon={saveState === 'loading' ? FiPlus : (saveState === 'success' ? FiCheck : (isError ? FiAlertCircle : FiPlus))} size={10} className={`${saveState === 'loading' ? 'animate-spin' : ''} mr-1`} />
                                              <span className="text-xs">{saveState === 'loading' ? 'Creating...' : (saveState === 'success' ? 'Created!' : (isError ? 'Failed' : 'Create'))}</span>
                                          </button>
                                      ) : isNewValue ? (
                                          <button
                                              onClick={() => saveValueToGlobal(key, currentValue)}
                                              disabled={saveState === 'loading'}
                                              className={`text-xs bg-neo-green hover:bg-green-300 border border-black px-3 py-2 min-w-[44px] min-h-[44px] flex items-center transition-all ${saveState === 'loading' ? 'opacity-80 cursor-wait' : ''} ${saveState === 'success' ? 'bg-green-500 text-white' : ''} ${saveState === 'failed' ? 'bg-red-500 text-white' : ''}`}
                                              title={saveState === 'loading' ? 'Saving...' : 'Add value to list'}
                                          >
                                              <SafeIcon icon={saveState === 'loading' ? FiSave : (saveState === 'success' ? FiCheck : (isError ? FiAlertCircle : FiSave))} size={10} className={`${saveState === 'loading' ? 'animate-spin' : ''} mr-1`} />
                                              <span className="text-xs">{saveState === 'loading' ? 'Saving...' : (saveState === 'success' ? 'Saved!' : (isError ? 'Failed' : 'Save Value'))}</span>
                                          </button>
                                      ) : null}
                                  </div>
                              )}
                         </div>

                        <div
                          ref={(node) => {
                            if (node) dropdownRefs.current[key] = node;
                          }}
                          className="relative"
                        >
                              <input
                                type="text"
                                value={variables[key]}
                                onChange={(e) => {
                                  handleVariableChange(key, e.target.value);
                                  if (storedValues && storedValues.length > 0) {
                                    setOpenDropdownVar(key);
                                  }
                                }}
                                onFocus={() => {
                                  setFocusedVar(key);
                                  setOpenDropdownVar(key);
                                }}
                                className="neo-input text-sm input-touch pr-20 w-full"
                                placeholder={storedValues ? "Select or type..." : "Type value..."}
                                autoComplete="off"
                              />

                              {storedValues && storedValues.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => toggleDropdown(key, storedValues)}
                                  className="absolute right-8 top-1/2 -translate-y-1/2 p-2 min-w-[44px] min-h-[44px] text-gray-600 hover:text-black flex items-center justify-center"
                                  aria-label="Toggle options"
                                >
                                  <SafeIcon icon={FiChevronDown} size={14} />
                                </button>
                              )}

                              {variables[key] && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleVariableChange(key, '');
                                    if (storedValues && storedValues.length > 0) {
                                      setOpenDropdownVar(key);
                                    }
                                  }}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 min-w-[44px] min-h-[44px] text-gray-400 hover:text-red-500 flex items-center justify-center"
                                  aria-label="Clear value"
                                >
                                  <SafeIcon icon={FiX} size={14} />
                                </button>
                              )}

                            {/* Custom Dropdown */}
                            {openDropdownVar === key && storedValues && storedValues.length > 0 && (
                              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border-2 border-black shadow-neo max-h-40 overflow-y-auto">
                                {filteredValues.map((val, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className="w-full text-left px-3 py-2 hover:bg-neo-yellow font-mono text-sm border-b border-gray-100 last:border-0"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleDropdownSelect(key, val)}
                                  >
                                    {val}
                                  </button>
                                ))}
                                {filteredValues.length === 0 && (
                                   <div className="px-3 py-2 text-gray-400 text-xs italic">No matching values</div>
                                )}
                              </div>
                            )}

                            {!storedValues && (
                                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                    <span className="text-xs border border-gray-300 px-1 rounded">NEW</span>
                                </div>
                            )}
                        </div>
                      </div>
                    );
                  })

               )}
             </div>
          </div>

          {/* Right Panel: Preview */}
          <div className="w-full md:w-2/3 flex flex-col bg-white">
              <div className="p-4 border-b-2 border-black bg-neo-green flex justify-between items-center">
                <h3 className="font-bold text-sm uppercase flex items-center">
                  <span className="bg-black text-white w-11 h-11 flex items-center justify-center rounded-full text-sm mr-2">2</span>
                  Preview & Copy
                </h3>
                <div className="flex items-center gap-2">
                  <button
                     onClick={() => setManualContent(null)}
                     className="p-2 min-w-[44px] min-h-[44px] border border-black bg-white hover:bg-gray-100 transition-colors shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-[1px] active:translate-y-[1px] flex items-center justify-center"
                     title="Reset to Template"
                  >
                    <SafeIcon icon={FiRotateCcw} size={14} />
                  </button>
                  <button
                     onClick={handleCopy}
                     className="bg-black text-white px-3 py-2 min-w-[44px] min-h-[44px] text-xs font-bold uppercase flex items-center hover:bg-gray-800 transition-colors shadow-[2px_2px_0px_0px_#ffffff]"
                  >
                    <SafeIcon icon={copied ? FiCheck : FiCopy} size={14} className="mr-1.5" />
                    {copied ? 'Copied!' : 'Copy Result'}
                  </button>
                </div>
            </div>

             <div className="flex-1 p-0 overflow-hidden relative">
                <textarea
                  className="w-full h-full p-4 sm:p-6 font-mono text-sm leading-relaxed resize-none focus:outline-none"
                  value={displayContent}
                  onChange={(e) => setManualContent(e.target.value)}
                  spellCheck={false}
                />
             </div>

             <div className="p-3 border-t-2 border-black bg-gray-100 flex justify-between items-center text-xs font-mono text-gray-500 gap-2">
                <div className="min-w-0">
                  {prompt.tags && prompt.tags.length > 0 && (
                    <div className="flex gap-1.5 flex-wrap">
                      {prompt.tags.map(tag => (
                        <span key={tag} className="bg-white border border-black px-1 text-xs text-black font-bold uppercase whitespace-nowrap">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                   <span className="hidden sm:inline">Last Modified: {prompt.updated_at ? format(new Date(prompt.updated_at), 'yyyy-MM-dd HH:mm') : 'Unknown'}</span>
                   <span className="sm:hidden">{prompt.updated_at ? format(new Date(prompt.updated_at), 'MM/dd HH:mm') : 'Unknown'}</span>
                </div>
             </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PromptDetailsModal;